import { createFileRoute, Link, useParams, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import {
  reportingPeriod,
  periodLabel,
  TRL_LABELS,
  type MonthlyReport,
} from "@/lib/monthly-report";
import { ArrowLeft, Save } from "lucide-react";

export const Route = createFileRoute("/manadsrapport/$companyId")({
  head: () => ({ meta: [{ title: "Månadsrapport — Inkubera" }] }),
  component: ReportForm,
});

type Form = Partial<MonthlyReport> & {
  crl?: number | null;
  brl?: number | null;
  tmrl?: number | null;
  srl?: number | null;
};

function ReportForm() {
  return (
    <AppShell>
      <Inner />
    </AppShell>
  );
}

function Inner() {
  const { companyId } = useParams({ from: "/manadsrapport/$companyId" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const period = useMemo(() => reportingPeriod(), []);
  const [companyName, setCompanyName] = useState("");
  const [form, setForm] = useState<Form>({});
  const [existingId, setExistingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: comp } = await supabase
        .from("companies")
        .select("name")
        .eq("id", companyId)
        .maybeSingle();
      setCompanyName(comp?.name ?? "");
      const { data: existing } = await supabase
        .from("monthly_reports")
        .select("*")
        .eq("company_id", companyId)
        .eq("year", period.year)
        .eq("month", period.month)
        .maybeSingle();
      if (existing) {
        setExistingId(existing.id);
        setForm(existing as MonthlyReport);
      }
      setLoading(false);
    })();
  }, [companyId, period.year, period.month]);

  const update = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const { data: prof } = await supabase
      .from("profiles")
      .select("full_name")
      .eq("id", user.id)
      .maybeSingle();
    const payload = {
      company_id: companyId,
      coach_user_id: user.id,
      coach_name: prof?.full_name ?? user.email ?? null,
      year: period.year,
      month: period.month,
      crl: form.crl ?? null,
      brl: form.brl ?? null,
      tmrl: form.tmrl ?? null,
      srl: form.srl ?? null,
      trl_comment: form.trl_comment ?? null,
      progress: form.progress ?? null,
      obstacles: form.obstacles ?? null,
      next_milestones: form.next_milestones ?? null,
      support_needed: form.support_needed ?? null,
      hours_logged: form.hours_logged ?? null,
      summary: form.summary ?? null,
    };
    if (existingId) {
      const { error } = await supabase
        .from("monthly_reports")
        .update(payload)
        .eq("id", existingId);
      if (error) {
        alert(error.message);
        setSaving(false);
        return;
      }
    } else {
      const { data, error } = await supabase
        .from("monthly_reports")
        .insert(payload)
        .select("id")
        .single();
      if (error) {
        alert(error.message);
        setSaving(false);
        return;
      }
      setExistingId(data.id);
    }
    setSavedAt(new Date().toLocaleTimeString("sv-SE"));
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Laddar…</div>;

  return (
    <div className="px-8 py-10 max-w-3xl">
      <Link
        to="/manadsrapport"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="size-3.5" /> Tillbaka
      </Link>

      <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
        {periodLabel(period.year, period.month)}
      </div>
      <h1 className="font-display text-3xl mb-1">{companyName}</h1>
      <p className="text-sm text-muted-foreground mb-8">
        TRL-utveckling och månadsrapport. Spara när du vill — du kan komma tillbaka och justera.
      </p>

      <section className="rounded-2xl border border-border bg-card p-6 mb-6">
        <h2 className="font-display text-lg mb-4">TRL-mognad</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {(["crl", "brl", "tmrl", "srl"] as const).map((k) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {TRL_LABELS[k]}
              </span>
              <select
                value={form[k] ?? ""}
                onChange={(e) => update(k, e.target.value === "" ? null : Number(e.target.value))}
                className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary text-sm"
              >
                <option value="">— välj nivå (1–9) —</option>
                {Array.from({ length: 9 }).map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
        <Field label="Kommentar TRL (valfritt)">
          <textarea
            value={form.trl_comment ?? ""}
            onChange={(e) => update("trl_comment", e.target.value)}
            rows={3}
            className="textarea"
          />
        </Field>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6 mb-6 space-y-4">
        <h2 className="font-display text-lg">Månadsrapport</h2>
        <Field label="Månadens framsteg">
          <textarea
            value={form.progress ?? ""}
            onChange={(e) => update("progress", e.target.value)}
            rows={3}
            className="textarea"
            placeholder="Vad har bolaget åstadkommit denna månad?"
          />
        </Field>
        <Field label="Hinder & utmaningar">
          <textarea
            value={form.obstacles ?? ""}
            onChange={(e) => update("obstacles", e.target.value)}
            rows={3}
            className="textarea"
          />
        </Field>
        <Field label="Milstolpar nästa månad">
          <textarea
            value={form.next_milestones ?? ""}
            onChange={(e) => update("next_milestones", e.target.value)}
            rows={3}
            className="textarea"
          />
        </Field>
        <Field label="Behov av stöd från Inkubera">
          <textarea
            value={form.support_needed ?? ""}
            onChange={(e) => update("support_needed", e.target.value)}
            rows={3}
            className="textarea"
          />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nedlagda timmar">
            <input
              type="number"
              min={0}
              step={0.5}
              value={form.hours_logged ?? ""}
              onChange={(e) => update("hours_logged", e.target.value === "" ? null : Number(e.target.value))}
              className="input"
            />
          </Field>
        </div>
        <Field label="Sammanfattning">
          <textarea
            value={form.summary ?? ""}
            onChange={(e) => update("summary", e.target.value)}
            rows={3}
            className="textarea"
            placeholder="Kort sammanfattning av månaden i två-tre meningar"
          />
        </Field>
      </section>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="h-10 px-5 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Save className="size-4" /> {saving ? "Sparar…" : existingId ? "Uppdatera" : "Skicka in"}
        </button>
        {savedAt && <span className="text-xs text-muted-foreground">Sparat {savedAt}</span>}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
